import { AlertSnippetDto, CustomerDto, CustomerType } from '../../../interfaces/IEvent'
import { AlertSendStatus } from '../../../interfaces/IAlert'

export type RecipientType = 'customer' | 'phone' | 'email'

export interface CustomerNotificationRow {
  customerNumber: string
  customerName: string
  customerType: CustomerType
  recipientType: RecipientType
  isNotified: boolean
  notifiedInAlerts: string[]
  pendingInAlerts: string[]
  draftInAlerts: string[]
  notifiedAlertIds: string[]
  pendingAlertIds: string[]
  draftAlertIds: string[]
}

function isCustomerInAlert(alert: AlertSnippetDto, customer: CustomerDto): boolean {
  return !!(
    (alert.notifiedCustomers?.allCompanyCustomers && (customer.customerType === CustomerType.Company || customer.customerType === CustomerType.CompanyWithSla)) ||
    alert.notifiedCustomers?.companyCustomerIds?.includes(customer.customerNumber) ||
    alert.notifiedCustomers?.privateCustomerIds?.includes(customer.customerNumber) ||
    alert.notifiedCustomers?.companyWithSlaCustomerIds?.includes(customer.customerNumber)
  )
}

export function computeNotificationStatus(
  affectedCustomers: CustomerDto[],
  alerts: AlertSnippetDto[],
  affectedPhoneNumbers: string[] = [],
  affectedEmailAddresses: string[] = [],
): CustomerNotificationRow[] {
  const sentAlerts = alerts.filter(
    (a) => (a.status === AlertSendStatus.Sent || a.status === AlertSendStatus.SentPartially) && a.notifiedCustomers,
  )

  const pendingAlerts = alerts.filter(
    (a) => (a.status === AlertSendStatus.NotProcessed || a.status === AlertSendStatus.Processing) && a.notifiedCustomers,
  )

  const draftAlerts = alerts.filter((a) => a.status === AlertSendStatus.Draft && a.notifiedCustomers)

  const customerRows: CustomerNotificationRow[] = affectedCustomers.map((customer) => {
    const matchingSent = sentAlerts.filter((alert) => isCustomerInAlert(alert, customer))
    const matchingPending = pendingAlerts.filter((alert) => isCustomerInAlert(alert, customer))
    const matchingDraft = draftAlerts.filter((alert) => isCustomerInAlert(alert, customer))

    return {
      customerNumber: customer.customerNumber,
      customerName: customer.customerName,
      customerType: customer.customerType,
      recipientType: 'customer' as const,
      isNotified: matchingSent.length > 0,
      notifiedInAlerts: matchingSent.map((a) => a.description || a.id),
      pendingInAlerts: matchingPending.map((a) => a.description || a.id),
      draftInAlerts: matchingDraft.map((a) => a.description || a.id),
      notifiedAlertIds: matchingSent.map((a) => a.id),
      pendingAlertIds: matchingPending.map((a) => a.id),
      draftAlertIds: matchingDraft.map((a) => a.id),
    }
  })

  const phoneRows: CustomerNotificationRow[] = affectedPhoneNumbers.map((phone) => {
    const matchingSent = sentAlerts.filter((a) => a.phoneNumbers?.includes(phone))
    const matchingPending = pendingAlerts.filter((a) => a.phoneNumbers?.includes(phone))
    const matchingDraft = draftAlerts.filter((a) => a.phoneNumbers?.includes(phone))

    return {
      customerNumber: phone,
      customerName: phone,
      customerType: CustomerType.Undefined,
      recipientType: 'phone' as const,
      isNotified: matchingSent.length > 0,
      notifiedInAlerts: matchingSent.map((a) => a.description || a.id),
      pendingInAlerts: matchingPending.map((a) => a.description || a.id),
      draftInAlerts: matchingDraft.map((a) => a.description || a.id),
      notifiedAlertIds: matchingSent.map((a) => a.id),
      pendingAlertIds: matchingPending.map((a) => a.id),
      draftAlertIds: matchingDraft.map((a) => a.id),
    }
  })

  const emailRows: CustomerNotificationRow[] = affectedEmailAddresses.map((email) => {
    const matchingSent = sentAlerts.filter((a) => a.emailAddresses?.includes(email))
    const matchingPending = pendingAlerts.filter((a) => a.emailAddresses?.includes(email))
    const matchingDraft = draftAlerts.filter((a) => a.emailAddresses?.includes(email))

    return {
      customerNumber: email,
      customerName: email,
      customerType: CustomerType.Undefined,
      recipientType: 'email' as const,
      isNotified: matchingSent.length > 0,
      notifiedInAlerts: matchingSent.map((a) => a.description || a.id),
      pendingInAlerts: matchingPending.map((a) => a.description || a.id),
      draftInAlerts: matchingDraft.map((a) => a.description || a.id),
      notifiedAlertIds: matchingSent.map((a) => a.id),
      pendingAlertIds: matchingPending.map((a) => a.id),
      draftAlertIds: matchingDraft.map((a) => a.id),
    }
  })

  return [...customerRows, ...phoneRows, ...emailRows]
}
